
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import "../Style/Account.css";
import { Eye, EyeOff } from "lucide-react";

const Account = () => {
  const [showChangedPassword, setShowChangedPassword] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const queryClient = useQueryClient();

  const API_URL = "https://api.yellowmatrix.co.in";

  const deviceStats = {
    total: 4,
    registered: 3,
    unregistered: 1,
  };

  const updateEmail = async () => {
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${API_URL}/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: newEmail,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to update email");
      }

      alert("Email updated successfully");

      setEditEmail(false);

      // 🔥 Refetch the user data
      queryClient.invalidateQueries(["accountUserData"]);
    } catch (error) {
      alert(error.message);
    }
  };

  const updatePassword = async () => {
    const token = localStorage.getItem("token");

    if (!oldPassword || !newPassword) {
      alert("Please fill both old and new password");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to update password");
      }

      alert("Password updated successfully!");
      setShowChangedPassword(false);
      setOldPassword("");
      setNewPassword("");
    } catch (error) {
      alert(error.message);
    }
  };

  const fetchUserData = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("No authentication token found. Please log in again.");
    }

    const response = await fetch(`${API_URL}/me`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorDetail = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorJson = JSON.parse(errorDetail);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch (e) {
        if (response.status === 401) {
          errorMessage =
            "Authentication failed. Your session might have expired.";
        } else {
          errorMessage = errorDetail || errorMessage;
        }
      }
      if (response.status === 401) {
        localStorage.removeItem("token");
        window.location.reload();
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
      username: data.Username || "N/A",
      email: data.Email || "N/A",
      location: data.Location || "N/A",
      company: data.CustomerName || "N/A",
      fullName: data.Username || "N/A",
      phone: data.Phone || "N/A",
      gst: data.GST || "N/A",
    };
  };

  const {
    data: userData,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ["accountUserData"],
    queryFn: fetchUserData,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  if (isError) {
    return (
      <div className="Account-container">
        <div className="Account-line">
          <h1 className="Account-title">Account</h1>
          <hr className="line" width="100%" />
        </div>
        <div className="error-message-Account">
          Error:{" "}
          {error?.message ||
            "An unknown error occurred while fetching account data."}
          {error?.message.includes("token") ||
          error?.message.includes("Authentication failed") ? (
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.reload();
              }}
              className="retry-btn"
            >
              Log in again
            </button>
          ) : (
            <button
              onClick={() => {
                window.location.reload();
              }}
              className="retry-btn"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="Account-container">
      <div className="Account-line">
        <h1 className="Account-title">Account</h1>
        <p className="Account-subtitle">
          Your account information and customization hub.
        </p>
        <hr className="line" width="100%" />
      </div>

      <div className="Account-grid">
        <div className="profile-card">
          <div className="profile-content">
            <div className="profile-info">
              <div
                className={`profile-avatar ${
                  isLoading ? "skeleton-effect" : ""
                }`}
              >
                {isLoading
                  ? ""
                  : userData?.username.charAt(0).toUpperCase() || "U"}
              </div>
              <div>
                <div
                  className={`profile-name ${
                    isLoading ? "skeleton-line skeleton-long" : ""
                  }`}
                >
                  {isLoading ? "" : userData?.username || "Username"}
                </div>
                <div
                  className={`profile-location ${
                    isLoading ? "skeleton-line skeleton-medium" : ""
                  }`}
                >
                  {isLoading ? (
                    ""
                  ) : (
                    <>
                      <span className="location-icon">📍</span>{" "}
                      {userData?.location || "Location"}
                    </>
                  )}
                </div>

                <div className="profile-details">
                  <div className="detail-item">
                    <span className="detail-icon">🏢</span>
                    <span className="detail-label">Customer ID:</span>
                    <span
                      className={
                        isLoading ? "skeleton-line skeleton-short" : ""
                      }
                    >
                      {isLoading ? "" : userData?.company || "Company Name"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">👤</span>
                    <span className="detail-label">User:</span>
                    <span
                      className={
                        isLoading ? "skeleton-line skeleton-medium" : ""
                      }
                    >
                      {isLoading ? "" : userData?.fullName || "Full Name"}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-icon">✉️</span>
                    <span className="detail-label">Email:</span>

                    {!editEmail ? (
                      <>
                        <span>{userData?.email || "email@example.com"}</span>

                        <span
                          className="edit-icon"
                          onClick={() => {
                            setEditEmail(true);
                            setNewEmail(userData?.email);
                          }}
                        >
                          ✏️
                        </span>
                      </>
                    ) : (
                      <div className="email-edit-strip">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="Enter new email"
                        />

                        <button className="save-btn" onClick={updateEmail}>
                          Save
                        </button>

                        <button
                          className="email-cancel-btn"
                          onClick={() => setEditEmail(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="detail-item">
                    <span className="detail-icon">📞</span>
                    <span className="detail-label">Phone:</span>
                    <span
                      className={
                        isLoading ? "skeleton-line skeleton-short" : ""
                      }
                    >
                      {isLoading ? "" : userData?.phone || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">🔒</span>
                    <span className="detail-label">Password:</span> ********
                  </div>

                  <div className="detail-item">
                    <span className="detail-icon">🧾</span>
                    <span className="detail-label">GST:</span>
                    <span
                      className={
                        isLoading ? "skeleton-line skeleton-medium" : ""
                      }
                    >
                      {isLoading ? "" : userData?.gst || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <button
              className={`change-password-btn ${
                isLoading ? "skeleton-effect" : ""
              }`}
              disabled={isLoading}
              onClick={() => setShowChangedPassword(!showChangedPassword)}
            >
              {isLoading ? "" : "Change Password"}
            </button>

            {showChangedPassword && (
              <div className="change-password-page">
                <div className="change-password-form">
                  <h3>Reset Password</h3>

                  {/* Old Password */}
                  <div className="password-input-wrapper">
                    <input
                      type={showOldPassword ? "text" : "password"}
                      placeholder="Old Password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                    />
                    <span
                      className="toggle-password"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                    >
                      {showOldPassword ? <EyeOff /> : <Eye />}
                    </span>
                  </div>

                  {/* New Password */}
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <span
                      className="toggle-password"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff /> : <Eye />}
                    </span>
                  </div>

                  <button className="submit-btn" onClick={updatePassword}>
                    Submit
                  </button>

                  <button
                    className="password-cancel-btn"
                    onClick={() => setShowChangedPassword(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="quick-actions-card">
          <div className="quick-actions-content">
            <div
              className={`quick-actions-title ${
                isLoading ? "skeleton-line skeleton-medium" : ""
              }`}
            >
              {isLoading ? "" : "Quick Actions"}
            </div>
            <button
              className={`quick-action-btn primary ${
                isLoading ? "skeleton-effect" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? "" : "View Reports"}
            </button>
            <button
              className={`quick-action-btn secondary ${
                isLoading ? "skeleton-effect" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? "" : "Manage Alerts"}
            </button>
            <button
              className={`quick-action-btn secondary ${
                isLoading ? "skeleton-effect" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? "" : "Track Vehicles"}
            </button>
          </div>
        </div>

        {/* Device Stats Cards (assuming these are static or loaded elsewhere, otherwise apply skeleton) */}
        <div className="device-card hoverable-card">
          <div className="device-content">
            <div>
              <div
                className={`device-label ${
                  isLoading ? "skeleton-line skeleton-short" : ""
                }`}
              >
                {isLoading ? "" : "Total Devices"}
              </div>
              <div
                className={`device-count ${
                  isLoading ? "skeleton-line skeleton-extra-short" : ""
                }`}
              >
                {isLoading ? "" : deviceStats.total}
              </div>
            </div>
            <div className="device-i-icon">
              <div className="device-icon">📱</div>
            </div>
          </div>
        </div>

        <div className="device-card hoverable-card">
          <div className="device-content">
            <div>
              <div
                className={`device-label ${
                  isLoading ? "skeleton-line skeleton-short" : ""
                }`}
              >
                {isLoading ? "" : "Registered Devices"}
              </div>
              <div
                className={`device-count ${
                  isLoading ? "skeleton-line skeleton-extra-short" : ""
                }`}
              >
                {isLoading ? "" : deviceStats.registered}
              </div>
            </div>
            <div className="registered-icon">
              <div className="check-icon">🔄</div>
            </div>
          </div>
        </div>

        <div className="device-card hoverable-card">
          <div className="device-content">
            <div>
              <div
                className={`device-label ${
                  isLoading ? "skeleton-line skeleton-short" : ""
                }`}
              >
                {isLoading ? "" : "Unregistered Devices"}
              </div>
              <div
                className={`device-count ${
                  isLoading ? "skeleton-line skeleton-extra-short" : ""
                }`}
              >
                {isLoading ? "" : deviceStats.unregistered}
              </div>
            </div>
            <div className="unregistered-icon">
              <div className="warning-icon">⚠️</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
